import React, { useRef } from "react";

import { a } from "@react-spring/web";

import { useIntersect } from "hooks/useIntersect";
import { useAnimateInTrail, IAnimateInProps } from "hooks/useAnimateInTrail";
import {
  initialOffset,
  animationTriggerThreshold,
} from "components/Animations/SpringProperties/SpringProperties";

import * as fromProjects from "store/Projects";

import { WorkDetailsHeroRoles } from "./WorkDetailsHeroRoles";
import { WorkDetailsHeroImage } from "./WorkDetailsHeroImage";

export interface IOwnSpecificProps {
  project: fromProjects.IProject;
}

type IOwnProps = IOwnSpecificProps & IAnimateInProps;

type IProps = IOwnProps;

const WorkDetailsHero: React.FC<IProps> = ({
  animationReady,
  project,
  ...props
}) => {
  const [inViewRef, inView] = useIntersect({
    startObserving: animationReady,
  });

  const [animateInTrail, triggerAnimateIn] = useAnimateInTrail(inView, 11, {
    notify: [
      {
        index: 4,
        threshold: animationTriggerThreshold,
      },
      {
        index: 10,
        threshold: animationTriggerThreshold,
      },
    ],
  });

  const wrapper = useRef<HTMLDivElement>(null);

  const wrapperStyles = {
    backgroundColor: project.heroSection.backgroundColor,
    backgroundImage:
      "url(/assets/images/work/" + project.images.detailsHeroBackground + ")",
  };

  const logoStyles = {
    opacity: animateInTrail[0].active,
    transform: animateInTrail[0].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const projectLogo =
    project.projectOverview.backgroundShade === "light"
      ? project.images.logoDark
      : project.images.logoLight;

  const titleStyles = {
    opacity: animateInTrail[1].active,
    transform: animateInTrail[1].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const separatorStyles = {
    opacity: animateInTrail[2].active,
    transform: animateInTrail[2].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const lineStyles = {
    width: animateInTrail[2].active.to((x) => `calc(${x} * 100%)`),
  };

  const descriptionStyles = {
    opacity: animateInTrail[3].active,
    transform: animateInTrail[3].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const overviewTitleStyles = {
    color: project.projectOverview.titleColor,
    opacity: animateInTrail[5].active,
    transform: animateInTrail[5].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const overviewDescriptionStyles = {
    opacity: animateInTrail[6].active,
    transform: animateInTrail[6].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const roleTitleStyles = {
    color: project.projectOverview.titleColor,
    opacity: animateInTrail[6].active,
    transform: animateInTrail[6].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const roleDescriptionStyles = {
    transform: animateInTrail[7].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const yearTitleStyles = {
    color: project.projectOverview.titleColor,
    opacity: animateInTrail[7].active,
    transform: animateInTrail[7].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const yearDescriptionStyles = {
    opacity: animateInTrail[8].active,
    transform: animateInTrail[8].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  return (
    <section
      ref={inViewRef}
      className="workDetailsHeroWrapper"
      style={wrapperStyles}
    >
      <div ref={wrapper}>
        <div className="headerWrapper">
          <a.img
            src={"/assets/images/work/" + projectLogo}
            alt={project.name}
            style={logoStyles}
          />
          <a.h1 style={titleStyles}>{project.title}</a.h1>
          <a.div className="separator" style={separatorStyles}>
            <a.div className="line" style={lineStyles} />
          </a.div>
          <a.p style={descriptionStyles}>{project.description}</a.p>
        </div>
        <div className="projectHeroSectionWrapper">
          <WorkDetailsHeroImage
            wrapperRef={wrapper}
            details={project.heroSection}
            backgroundImage={project.images.detailsHeroFeaturedBackground}
            animationReady={triggerAnimateIn(4)}
          />
        </div>
        <div className="projectOverviewWrapper">
          <div className="overviewWrapper">
            <a.h1 style={overviewTitleStyles}>Overview</a.h1>
            <a.p style={overviewDescriptionStyles}>
              {project.projectOverview.overview}
            </a.p>
          </div>
          <div className="roleWrapper">
            <a.h1 style={roleTitleStyles}>Our Role</a.h1>
            <a.div style={roleDescriptionStyles}>
              <WorkDetailsHeroRoles
                roles={project.projectOverview.role}
                animateIn={triggerAnimateIn(10)}
              />
            </a.div>
          </div>
          <div className="yearWrapper">
            <a.h1 style={yearTitleStyles}>Year</a.h1>
            <a.p style={yearDescriptionStyles}>
              {project.projectOverview.year}
            </a.p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Wire up the React component to the pageSection
export default WorkDetailsHero;
